import { useState, useEffect, useRef, useCallback } from 'react';

interface ActivityEvent {
  id: string;
  type: 'entity' | 'alert' | 'investigation' | 'report';
  message: string;
  timestamp: string;
}

const EVENT_ICONS: Record<string, { icon: string; color: string }> = {
  entity: { icon: '+', color: 'text-cyber-cyan bg-cyber-cyan/10' },
  alert: { icon: '!', color: 'text-amber-400 bg-amber-400/10' },
  investigation: { icon: '?', color: 'text-purple-400 bg-purple-400/10' },
  report: { icon: 'R', color: 'text-green-400 bg-green-400/10' },
};

export default function ActivityFeedWidget() {
  const [events, setEvents] = useState<ActivityEvent[]>([]);
  const wsRef = useRef<WebSocket | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const addEvent = useCallback((evt: ActivityEvent) => {
    setEvents((prev) => [evt, ...prev].slice(0, 50));
  }, []);

  useEffect(() => {
    // Try to connect to WebSocket for real-time events
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    // ⚠️⚠️ `hostname` + `:8000` NE PEUT PAS MARCHER DEPUIS UN NAVIGATEUR.
    // 8000 est le port INTERNE de l'API ; sur Internet, seul 443 est ouvert et
    // c'est Traefik puis nginx qui acheminent `/ws`. Cette URL visait donc un
    // port fermé — et, comme elle change d'ORIGINE (port différent), la CSP la
    // refusait AVANT même la tentative de connexion. Mesuré le 14/09/2026.
    // `host` porte l'hôte ET le port réel de la page : même origine, quelle que
    // soit la marque qui sert le bundle.
    const wsUrl = `${protocol}//${window.location.host}/ws/activity`;

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data) as ActivityEvent;
          addEvent(data);
        } catch {
          // Ignore malformed messages
        }
      };

      ws.onerror = () => {
        // WebSocket not available, use mock data
        generateMockEvents();
      };

      ws.onclose = () => {
        // Fallback to mock events if connection closes
        generateMockEvents();
      };
    } catch {
      generateMockEvents();
    }

    return () => {
      wsRef.current?.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const generateMockEvents = () => {
    const types: ActivityEvent['type'][] = ['entity', 'alert', 'investigation', 'report'];
    const messages: Record<string, string[]> = {
      entity: [
        'Nouvelle entite ajoutee: Organisation Alpha',
        'Entite mise a jour: IP 192.168.1.1',
        'Nouvelle relation detectee entre Person-X et Org-Y',
      ],
      alert: [
        'Alerte critique: Activite suspecte detectee',
        'Nouvelle alerte: Transaction anormale',
        'Alerte elevee: Connexion depuis pays a risque',
      ],
      investigation: [
        'Investigation ouverte: Cas #2024-1042',
        'Analyse terminee: Reseau financier suspect',
        'Investigation mise a jour: Nouvelles preuves',
      ],
      report: [
        'Rapport genere: Analyse mensuelle',
        'Rapport exporte: Resume des menaces',
        'Nouveau rapport: Conformite AML Q4',
      ],
    };

    // Seed with initial events
    const initial: ActivityEvent[] = [];
    for (let i = 0; i < 8; i++) {
      const type = types[Math.floor(Math.random() * types.length)];
      const typeMessages = messages[type];
      initial.push({
        id: `mock-${i}`,
        type,
        message: typeMessages[Math.floor(Math.random() * typeMessages.length)],
        timestamp: new Date(Date.now() - i * 60000 * Math.random() * 10).toISOString(),
      });
    }
    setEvents(initial);

    // Add new events periodically
    const interval = setInterval(() => {
      const type = types[Math.floor(Math.random() * types.length)];
      const typeMessages = messages[type];
      addEvent({
        id: `mock-${Date.now()}`,
        type,
        message: typeMessages[Math.floor(Math.random() * typeMessages.length)],
        timestamp: new Date().toISOString(),
      });
    }, 8000);

    return () => clearInterval(interval);
  };

  return (
    <div className="glass-panel p-4 h-full flex flex-col">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-300">Activite en direct</h3>
        <div className="flex items-center gap-1.5">
          <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
          <span className="text-[10px] text-gray-500">Live</span>
        </div>
      </div>
      <div ref={containerRef} className="flex-1 overflow-y-auto space-y-1.5 min-h-0">
        {events.map((evt) => {
          const style = EVENT_ICONS[evt.type] ?? EVENT_ICONS.entity;
          return (
            <div
              key={evt.id}
              className="flex items-start gap-2 p-2 rounded-md hover:bg-surface-tertiary/30 transition-colors animate-fade-in"
            >
              <div
                className={`flex-shrink-0 w-5 h-5 rounded text-[10px] font-bold flex items-center justify-center ${style.color}`}
              >
                {style.icon}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-300 leading-relaxed">{evt.message}</p>
                <p className="text-[10px] text-gray-600 mt-0.5">
                  {new Date(evt.timestamp).toLocaleTimeString('fr-FR', {
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                  })}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
